import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import { brand, neutral } from '../theme';
import { DEFAULT_FPS, formatTimecode } from '../utils/timecode';

interface AssetPreviewModalProps {
  open: boolean;
  onClose: () => void;
  asset: {
    id?: string;
    url: string;
    type: 'image' | 'audio';
    name?: string;
    startTime?: number;
    endTime?: number;
    position?: string;
    notes?: string;
  } | null;
  onDownload: (assetId: string, retryCount?: number) => Promise<{ success: boolean; downloadUrl?: string; filename?: string; error?: string }>;
  /** Frame rate for the placement timecodes (defaults to 30 fps). */
  fps?: number;
}

/** A look at one image or audio file the creator attached, with a download. */
const AssetPreviewModal: React.FC<AssetPreviewModalProps> = ({
  open,
  onClose,
  asset,
  onDownload,
  fps = DEFAULT_FPS,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // A fresh start for each file.
  useEffect(() => {
    setError(null);
    setPreviewFailed(false);
    setRetryCount(0);
  }, [asset?.url]);

  const handleDownload = async () => {
    if (!asset?.id) return;

    setDownloading(true);
    setError(null);

    try {
      const result = await onDownload(asset.id, retryCount);

      if (result.success && result.downloadUrl) {
        // Trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.filename || `asset-${asset.id}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Close modal after successful download
        setTimeout(() => onClose(), 500);
      } else {
        setError(result.error || 'The download didn’t start. Please try again.');
        setRetryCount((count) => count + 1);
      }
    } catch (err: any) {
      console.error('Download failed:', err);
      setError('The download didn’t start. Please try again.');
      setRetryCount((count) => count + 1);
    } finally {
      setDownloading(false);
    }
  };

  if (!asset) return null;

  const isImage = asset.type === 'image';
  const kindLabel = isImage ? 'Image' : 'Audio';
  const title = asset.name || kindLabel;
  // The workspace names a file after its note, so only show a note that adds something.
  const notes = asset.notes && asset.notes.trim() !== title.trim() ? asset.notes : null;
  const hasStart = typeof asset.startTime === 'number' && Number.isFinite(asset.startTime);
  const hasEnd = hasStart && typeof asset.endTime === 'number' && Number.isFinite(asset.endTime) && (asset.endTime as number) > (asset.startTime as number);
  const facts = [
    kindLabel,
    hasStart
      ? `${formatTimecode(asset.startTime as number, fps)}${hasEnd ? ` → ${formatTimecode(asset.endTime as number, fps)}` : ''}`
      : null,
    asset.position ? `Position: ${asset.position}` : null,
  ].filter(Boolean);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth aria-labelledby="asset-preview-title">
      <DialogTitle id="asset-preview-title" sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7, fontWeight: 700 }}>
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: brand.slate }}>
          {isImage ? <ImageOutlinedIcon /> : <AudioFileOutlinedIcon />}
        </Box>
        <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Box>
      </DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
        <CloseOutlinedIcon />
      </IconButton>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void handleDownload()} disabled={downloading}>
                Try again
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: notes ? 0.5 : 2, fontVariantNumeric: 'tabular-nums' }}>
          {facts.join(' · ')}
        </Typography>
        {notes && (
          <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {notes}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: isImage ? 300 : 140,
            bgcolor: neutral[100],
            border: `1px solid ${brand.border}`,
            borderRadius: 3,
            p: 2,
          }}
        >
          {previewFailed ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {isImage ? 'This image can’t be shown here.' : 'This audio can’t be played here.'} Download it to open it on your computer.
            </Typography>
          ) : isImage ? (
            <Box
              component="img"
              src={asset.url}
              alt={asset.notes || asset.name || 'Attached image'}
              onError={() => setPreviewFailed(true)}
              sx={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', borderRadius: 2, display: 'block' }}
            />
          ) : (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              controls
              src={asset.url}
              aria-label={`Play ${title}`}
              style={{ width: '100%' }}
              onError={() => setPreviewFailed(true)}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          variant="contained"
          onClick={() => void handleDownload()}
          disabled={downloading || !asset.id}
          startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
        >
          {downloading ? 'Downloading…' : 'Download'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetPreviewModal;
